import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { SEARCE_COLORS } from '../../../theme';
import { SKELETON_ANIMATION, skeletonTintSx } from './skeletonTokens';

export interface PageLoadingSkeletonProps {
  /** Shown under the skeleton blocks */
  message?: string;
  /**
   * `fullscreen` — whole viewport + optional sidebar rail (auth bootstrap, standalone).
   * `embedded` — main column only (inside `DashboardLayout` where the real sidebar already exists).
   */
  variant?: 'fullscreen' | 'embedded';
}

const MainColumnSkeleton: React.FC<{ message?: string }> = ({ message }) => (
  <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
    <Skeleton
      variant="text"
      animation={SKELETON_ANIMATION}
      width="35%"
      height={36}
      sx={{ mb: 2, ...skeletonTintSx }}
    />
    <Skeleton
      variant="text"
      animation={SKELETON_ANIMATION}
      width="55%"
      height={24}
      sx={{ mb: 3, ...skeletonTintSx }}
    />
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
      {[1, 2, 3, 4].map((k) => (
        <Skeleton
          key={k}
          variant="rounded"
          animation={SKELETON_ANIMATION}
          height={96}
          sx={{ flex: '1 1 160px', minWidth: 140, maxWidth: 220, ...skeletonTintSx }}
        />
      ))}
    </Box>
    <Skeleton
      variant="rounded"
      animation={SKELETON_ANIMATION}
      height={280}
      sx={{ borderRadius: 2, ...skeletonTintSx }}
    />
    {message != null && message !== '' && (
      <Typography
        variant="body2"
        sx={{ mt: 2, textAlign: 'center', color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}
      >
        {message}
      </Typography>
    )}
  </Box>
);

/**
 * Full-viewport placeholder for auth gate and standalone bootstrapping.
 * Use `variant="embedded"` when this sits inside the dashboard shell (sidebar already visible).
 */
export const PageLoadingSkeleton: React.FC<PageLoadingSkeletonProps> = ({
  message,
  variant = 'fullscreen',
}) => {
  if (variant === 'embedded') {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 140px)',
          bgcolor: SEARCE_COLORS.background,
        }}
      >
        <MainColumnSkeleton message={message} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: SEARCE_COLORS.background,
      }}
    >
      <Skeleton
        variant="rounded"
        animation={SKELETON_ANIMATION}
        sx={{
          width: { xs: 0, sm: 72 },
          display: { xs: 'none', sm: 'block' },
          borderRadius: 0,
          ...skeletonTintSx,
        }}
      />
      <MainColumnSkeleton message={message} />
    </Box>
  );
};
